import type { SortOption } from '@/components/common/content-filter/use-content-filter'

// Shared shape for both /gallery and /projects listing cards. Each page's
// server component flattens its Prisma result into this before handing it to
// the client. Deliberately narrower than ProjectItem: only what the card and
// the filter bar's search/sort/category logic touch.
export interface PortfolioProject {
  id: string
  slug: string
  title: string | null
  description: string
  categorySlug: string | null
  categoryName: string | null
  isFeatured: boolean
  isSponsored: boolean
  media: { url: string; type: string }[]
  keywords: string[]
  createdAt: string
}

// Commercial work is tagged with this keyword rather than a dedicated column:
// the enrichment pass writes it into Project.keywords, so both listings and the
// detail page read the same field and no migration is needed to reclassify a
// job. Categories stay orthogonal (a branded truck can be a vinyl wrap, a fleet
// wrap, or commercial graphics and still be commercial work).
export const COMMERCIAL_KEYWORD = 'commercial'

export function isCommercialProject(project: Pick<PortfolioProject, 'keywords'>): boolean {
  return (project.keywords || []).includes(COMMERCIAL_KEYWORD)
}

// These categories are commercial by definition, so a Commercial badge next to
// the category chip just says the same thing twice. The badge earns its space
// only where the category does not already imply it, such as a branded truck
// filed under vinyl wraps.
const CATEGORIES_IMPLYING_COMMERCIAL = new Set(['commercial-graphics', 'fleet-wraps'])

export function showsCommercialBadge(
  project: Pick<PortfolioProject, 'keywords' | 'categorySlug'>
): boolean {
  return (
    isCommercialProject(project) &&
    !CATEGORIES_IMPLYING_COMMERCIAL.has(project.categorySlug ?? '')
  )
}

export type WorkType = 'all' | 'commercial' | 'personal'

export interface PortfolioCategory {
  name: string
  slug: string
}

export const PORTFOLIO_SORT_OPTIONS: SortOption<PortfolioProject>[] = [
  {
    label: 'Newest',
    value: 'newest',
    compareFn: (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  },
  {
    label: 'Oldest',
    value: 'oldest',
    compareFn: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  },
  {
    label: 'A–Z',
    value: 'az',
    compareFn: (a, b) => (a.title || '').localeCompare(b.title || ''),
  },
  {
    label: 'Z–A',
    value: 'za',
    compareFn: (a, b) => (b.title || '').localeCompare(a.title || ''),
  },
]
